import React, { useState } from "react";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

interface RoomExamStep3PreferencesProps {
  onBack: () => void;
  onSkip: () => void;
  onStart: (data: {
    questionCount: number;
    questionTypes: string[];
    examDuration: number | null;
  }) => void;
}

const QUESTION_TYPE_OPTIONS = [
  "Multiple Choice",
  "True/False",
  "Short Answer",
  "Fill in the Blank",
];

export const RoomExamStep3Preferences: React.FC<RoomExamStep3PreferencesProps> = ({
  onBack,
  onSkip,
  onStart,
}) => {
  const [questionCount, setQuestionCount] = useState<number>(25);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [examDuration, setExamDuration] = useState<number | null>(null);

  const toggleQuestionType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleStart = () => {
    if (selectedTypes.length === 0) {
      return; // Prevent submission if no question types selected
    }
    onStart({
      questionCount,
      questionTypes: selectedTypes,
      examDuration,
    });
  };

  return (
    <section className="mb-6 flex w-full items-center justify-center">
      <div className="flex w-full flex-col items-center gap-4 rounded-3xl border border-border bg-card px-8 py-6 shadow-sm lg:px-16">
        
        {/* Stepper */}
        <div className="flex w-full items-center justify-center mb-3">
          <div className="flex w-1/3 max-w-sm gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-muted">
              <div className="h-1.5 w-full rounded-full bg-correct" />
            </div>
            <div className="h-1.5 flex-1 rounded-full bg-muted">
              <div className="h-1.5 w-full rounded-full bg-correct" />
            </div>
            <div className="h-1.5 flex-1 rounded-full bg-muted">
              <div className="h-1.5 w-full rounded-full bg-correct" />
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="flex max-w-2xl flex-col items-center gap-1 text-center">
          <h2 className="text-2xl sm:text-3xl font-medium">
            Choose your preference
          </h2>
        </div>

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleStart();
          }}
          className="flex flex-col items-center gap-5 w-full max-w-2xl mt-2"
        >
          {/* Row 1: Question count + Duration */}
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            {/* Number of Questions */}
            <div className="flex flex-col flex-1">
              <label className="text-sm font-medium text-foreground mb-1.5">
                Number of Questions<span className="text-incorrect">*</span>
              </label>
              <input
                type="number"
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value))}
                placeholder="e.g., 25"
                min="1"
                required
                className="h-11 rounded-xl border-2 border-border px-4 text-base bg-background text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none transition-colors"
              />
            </div>

            {/* Exam Duration */}
            <div className="flex flex-col flex-1">
              <label className="text-sm font-medium text-foreground mb-1.5">
                Exam Length (Minutes)
              </label>
              <input
                type="number"
                value={examDuration ?? ""}
                onChange={(e) =>
                  setExamDuration(e.target.value ? Number(e.target.value) : null)
                }
                placeholder="e.g., 60"
                min="1"
                className="h-11 rounded-xl border-2 border-border px-4 text-base bg-background text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* Row 2: Question Types */}
          <div className="w-full">
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Question Type<span className="text-incorrect">*</span>
            </label>
            {selectedTypes.length === 0 && (
              <p className="text-xs text-incorrect mb-1">Please select at least one question type</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {QUESTION_TYPE_OPTIONS.map((type) => {
                const isSelected = selectedTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleQuestionType(type)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                      transition-all duration-200
                      ${
                        isSelected
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }
                    `}
                  >
                    {type}
                    {isSelected && <X className="h-3 w-3" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-3 flex gap-4 mb-2">
            <button
              type="button"
              onClick={onBack}
              className="flex h-10 w-12 items-center justify-center rounded-xl border border-border text-foreground hover:bg-muted transition"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <button
              type="submit"
              disabled={selectedTypes.length === 0}
              className="inline-flex h-10 flex-1 items-center justify-center gap-1 rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Start
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>

      </div>
    </section>
  );
};
